import { Loader2, ArrowRight, ArrowLeft, FileText, Link2, X } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { useConnectedGraphs } from './useConnectedGraphs'
import type { SheetConnector } from '@/types/sheet-connector'

interface ConnectedPnidsPanelV2Props {
  isOpen: boolean
  onClose: () => void
  graphId: string
  /**
   * Open a connected target. Routed through the editor's existing
   * drawing-connector click handler so navigation context (pnidPages,
   * multiPageSet) is preserved.
   */
  onOpenTarget: (label: string, extraProperties?: Record<string, string>) => void
  /** Manual sheet-connector click (same handler as the canvas edge buttons). */
  onManualConnectorClick?: (connector: SheetConnector) => void
}

export default function ConnectedPnidsPanelV2({
  isOpen,
  onClose,
  graphId,
  onOpenTarget,
  onManualConnectorClick,
}: ConnectedPnidsPanelV2Props) {
  const { t } = useTranslation()
  const { data, loading, error } = useConnectedGraphs(graphId, isOpen)

  if (!isOpen) return null

  const pages = data?.pages ?? []
  const siblings = data?.siblings ?? []
  const connections = data?.connections ?? []
  const manualConnectors = data?.manual_connectors ?? []
  const showPages = pages.length > 1
  const showSiblings = siblings.length > 1
  // "Empty" must mean no SECTION renders, which is not the same question as
  // hasConnectedPnids() — that one gates the navbar entry point on real
  // connections only. Deriving this from it printed "No connected P&IDs found."
  // directly above a populated "Pages of this PDF" list.
  const isEmpty =
    !loading &&
    !error &&
    !showPages &&
    !showSiblings &&
    connections.length === 0 &&
    manualConnectors.length === 0

  const rowClass =
    'w-full flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm hover:bg-gray-100 dark:hover:bg-dark-accent transition-colors'

  return (
    <div
      className="w-[391px] h-full border-l border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card flex flex-col shrink-0 overflow-hidden"
      data-testid="connected-pnids-panel"
    >
      {/* Header */}
      <div className="px-6 py-4 shrink-0 flex items-center justify-between">
        <h3 className="text-base font-semibold text-foreground">
          {t('graph_editor:connectedPnids.title', { defaultValue: 'Connected P&IDs' })}
        </h3>
        <button onClick={onClose} aria-label={t('graph_editor:connectedPnids.close', { defaultValue: 'Close' })}>
          <X className="w-4 h-4 text-muted-foreground hover:text-foreground" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 pb-4">
        {loading && (
          <div className="flex justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        )}
        {error && !loading && (
          <p className="text-sm text-muted-foreground py-4">
            {t('graph_editor:connectedPnids.error', { defaultValue: 'Could not load connected P&IDs.' })}
          </p>
        )}
        {isEmpty && (
          <p className="text-sm text-muted-foreground py-4">
            {t('graph_editor:connectedPnids.empty', { defaultValue: 'No connected P&IDs found.' })}
          </p>
        )}

        <div className="flex flex-col gap-5">
          {showPages && (
            <section>
              <h4 className="text-sm font-semibold text-foreground mb-1.5">
                {t('graph_editor:connectedPnids.pages', { defaultValue: 'Pages of this PDF' })}
              </h4>
              {pages.map((page) => (
                <button
                  key={page.graph_id}
                  className={`${rowClass} ${page.is_current ? 'bg-primary/5 text-primary' : ''}`}
                  disabled={page.is_current}
                  onClick={() =>
                    onOpenTarget(t('graph_editor:connectedPnids.page', { page: page.page, defaultValue: `Page ${page.page}` }), {
                      connected_to_graph_id: page.graph_id,
                      ...(data?.document_id ? { connected_to_document_id: data.document_id } : {}),
                    })
                  }
                >
                  <FileText className="w-4 h-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">
                    {t('graph_editor:connectedPnids.page', { page: page.page, defaultValue: `Page ${page.page}` })}
                  </span>
                </button>
              ))}
            </section>
          )}

          {showSiblings && (
            <section>
              <h4 className="text-sm font-semibold text-foreground mb-1.5">
                {t('graph_editor:connectedPnids.sheets', { defaultValue: 'Sheets in this set' })}
              </h4>
              {siblings.map((sibling) => (
                <button
                  key={sibling.document_id}
                  className={`${rowClass} ${sibling.is_current ? 'bg-primary/5 text-primary' : ''}`}
                  disabled={sibling.is_current || !sibling.graph_id}
                  title={
                    sibling.graph_id
                      ? undefined
                      : t('graph_editor:connectedPnids.processing', { defaultValue: 'Still processing' })
                  }
                  onClick={() =>
                    sibling.graph_id &&
                    onOpenTarget(sibling.name ?? '', {
                      connected_to_graph_id: sibling.graph_id,
                      connected_to_document_id: sibling.document_id,
                    })
                  }
                >
                  <FileText className="w-4 h-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">{sibling.name ?? sibling.document_id}</span>
                </button>
              ))}
            </section>
          )}

          {connections.length > 0 && (
            <section>
              <h4 className="text-sm font-semibold text-foreground mb-1.5">
                {t('graph_editor:connectedPnids.connections', { defaultValue: 'Connected drawings' })}
              </h4>
              {connections.map((conn) => (
                <button
                  key={`${conn.source_node_id}:${conn.connected_graph_id}`}
                  className={rowClass}
                  onClick={() =>
                    onOpenTarget(conn.source_label ?? '', {
                      connected_to_graph_id: conn.connected_graph_id,
                      ...(conn.connected_node_id ? { connected_to_node_id: conn.connected_node_id } : {}),
                      ...(conn.connected_document_id
                        ? { connected_to_document_id: conn.connected_document_id }
                        : {}),
                    })
                  }
                >
                  {conn.direction === 'inlet' ? (
                    <ArrowLeft className="w-4 h-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <ArrowRight className="w-4 h-4 shrink-0 text-muted-foreground" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{conn.source_label ?? conn.connected_graph_id}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {conn.connected_page !== null
                        ? t('graph_editor:connectedPnids.page', {
                            page: conn.connected_page,
                            defaultValue: `Page ${conn.connected_page}`,
                          })
                        : (conn.connected_document_name ?? '')}
                    </span>
                  </span>
                </button>
              ))}
            </section>
          )}

          {manualConnectors.length > 0 && (
            <section>
              <h4 className="text-sm font-semibold text-foreground mb-1.5">
                {t('graph_editor:connectedPnids.manual', { defaultValue: 'Manual connectors' })}
              </h4>
              {manualConnectors.map((connector) => (
                <button
                  key={connector.id}
                  className={rowClass}
                  onClick={() => onManualConnectorClick?.(connector)}
                >
                  <Link2 className="w-4 h-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">{connector.label || connector.connector_id}</span>
                </button>
              ))}
            </section>
          )}
        </div>
      </div>
    </div>
  )
}
